import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import type { ReleaseReelsPlan } from '../../../../../../redux/api/fymAPI.types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { StudioActionError } from '../../../studio/StudioActionError';
import { launchMessages } from '../rollout/launch/launchMessages';
import { useRollout } from '../rollout/RolloutContext';
import type { PlanStepView } from '../rollout/rolloutModel';
import { planDayDate } from '../rolloutDayCopy';
import { reelsPlanMessages } from './reelsPlanMessages';
import { reelsPlanProgress } from './reelsPlanModel';
/* eslint-disable css-modules/no-unused-class -- each part uses its own slice of the two stylesheets */
import rolloutStyles from '../rollout/Rollout.module.scss';
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanHeadProps {
  reelsPlan: ReleaseReelsPlan;
  steps: PlanStepView[];
  /** The count of the drafts that exist now: "Schedule all N posts". */
  draftCount: number;
  /** Below 700 px: "Schedule all" is the sticky bar of the run sheet, and the link reads "End". */
  isNarrow: boolean;
  onScheduleAll: () => void;
  onEnd: () => void;
}

/**
 * The head of the run sheet of an AI reels plan (design rollout-plan f3, f6): the settings ("14
 * days · every 2 days · from Tue 8 Jun"; "· 2 posts a day" when the plan has more than 1), the count of ready reels with its bar, "Schedule all N
 * posts" while drafts exist, and "End the plan".
 */
const ReelsPlanHeadComponent: React.FC<ReelsPlanHeadProps> = ({
  reelsPlan,
  steps,
  draftCount,
  isNarrow,
  onScheduleAll,
  onEnd,
}) => {
  const intl = useIntl();
  const { actions, canAct } = useRollout();
  const progress = useMemo(() => reelsPlanProgress(steps), [steps]);

  return (
    <div className={styles.head}>
      <div className={styles.headText}>
        <span className={styles.headKicker}>{intl.formatMessage(reelsPlanMessages.title)}</span>
        <span className={styles.headSub}>
          {intl.formatMessage(
            (reelsPlan.postsPerDay ?? 1) > 1 ? reelsPlanMessages.settingsLinePosts : reelsPlanMessages.settingsLine,
            {
              days: reelsPlan.days,
              cadence: reelsPlan.cadence,
              posts: reelsPlan.postsPerDay,
              from: planDayDate(intl, reelsPlan.startDay),
            }
          )}
        </span>
      </div>
      <span className={styles.grow} />
      <span className={styles.progress}>
        {intl.formatMessage(reelsPlanMessages.readyCount, { ready: progress.ready, count: progress.count })}
        <span className={styles.bar} aria-hidden>
          <i style={{ width: `${progress.percent}%` }} />
        </span>
      </span>
      {!isNarrow && draftCount > 0 && (
        <SMLButton variant="primary" disabled={!canAct || actions.busy !== null} onClick={onScheduleAll}>
          {intl.formatMessage(launchMessages.scheduleAll, { count: draftCount })}
        </SMLButton>
      )}
      <button type="button" className={rolloutStyles.link} disabled={!canAct} onClick={onEnd}>
        {intl.formatMessage(isNarrow ? reelsPlanMessages.endShort : reelsPlanMessages.endPlan)}
      </button>
      {(actions.feedback.plan ?? null) !== null && (
        <div className={styles.headFeedback}>
          <StudioActionError feedback={actions.feedback.plan ?? null} />
        </div>
      )}
    </div>
  );
};

export const ReelsPlanHead = TypedMemo(ReelsPlanHeadComponent);
